import {Item} from "./Item";
import {Product} from "../Product";
import {Draft} from "../Draft";

export class ProductItem extends Item {

  private base: Product;

  constructor( product: Product ) {
    super();
    this.base = product;
  }

  getBindingType(): string {
    if ( this.base.book ) {
      return this.base.book.bindingType;
    }
    return this.base.productBindingType || '';
  }
  getBase(): any {
    return this.base;
  }
  // NOTE: do NOT override Item.getCost() here. The base class drives
  // cost off the fetched ProductionRateTable (via `computeCost()` and
  // the cached `this.cost`); overriding to return a hard-coded 0
  // silently masks the real price, which is the cause of the
  // "cart shows $0.00" bug encountered 2026-06-25.

  getCoverImage(): string {
    return this.base.productCoverImage || "";
  }

  getHeight(): number {
    if ( this.base.book && this.base.book.body ) {
      return this.base.book.body.height / 72;
    }
    // Fallback: Product.productHeight is stored in POINTS on the server
    // (see product.component.html which divides by 72 for display).
    // Divide here so the shipping / rate payloads always see inches,
    // matching the book.body path and DraftItem.getHeight.
    return (this.base.productHeight || 0) / 72;
  }

  getId(): number {
    return this.base.getId();
  }

  getInteriorType(): string {
    if ( this.base.book ) {
      return this.base.book.interiorType;
    }
    return this.base.productInteriorType || '';
  }

  getLaminateType(): string {
    if ( this.base.book && this.base.book.cover ) {
      return this.base.book.cover.laminateType;
    }
    return "";
  }

  getPages(): number {
    if ( this.base.book && this.base.book.body ) {
      return this.base.book.body.numPages;
    }
    return this.base.productNumPages || 0;
  }

  getPaperType(): number {
    if ( this.base.book && this.base.book.body ) {
      return this.base.book.body.paperType_ID;
    }
    return 0;
  }

  getTitle(): string {
    return this.base.title;
  }

  getWeight(): number {
    if ( this.base.book ) {
      return this.base.book.weight;
    }
    return this.base.productWeight || 0;
  }

  getWidth(): number {
    if ( this.base.book && this.base.book.body ) {
      return this.base.book.body.width / 72;
    }
    // Fallback: Product.productWidth is stored in POINTS on the server
    // (see product.component.html which divides by 72 for display).
    // Divide here so the shipping / rate payloads always see inches,
    // matching the book.body path and DraftItem.getWidth.
    return (this.base.productWidth || 0) / 72;
  }
  getThickness(): number {
    if ( this.base.book ) {
      return this.base.book.thickness;
    }
    return 0;
  }

  getCaseQuantity(): number {
    if ( this.base.book ) {
      return this.base.book.caseQuantity;
    }
    return 0;
  }

  getLastUpdate(): Date {
    return this.base.lastUpdate;
  }

  getAvailableInventory(): any[] {
    const availableInventory = [];
    if ( this.base.facilityInventoryProducts ) {
      for ( const facilityInventoryProduct of this.base.facilityInventoryProducts ) {
        if ( facilityInventoryProduct.quantityOnHand - facilityInventoryProduct.quantityCommited > 0 ) {
          availableInventory.push( { 'facility': '' + facilityInventoryProduct.printnode_ID, 'quantity': facilityInventoryProduct.quantityOnHand - facilityInventoryProduct.quantityCommited } );
        }
      }
    }
    return availableInventory;
  }

  /**
   * Completeness check for the production-rate + shipping payloads.
   *
   * `ProductItem` deliberately does NOT implement a real gate here —
   * historically `OrderService.getProductionRatesForItem` skips the
   * `hasCompleteRateInputs` check for any Item that doesn't define the
   * method, and the pre-2026-07-09 build relied on that "always fire"
   * behavior for storefront Products. This class briefly gained a real
   * gate that required `product.book` to be attached, but `BookService`
   * is never proactively loaded on the cart page (`ProductService`
   * pulls Products only; Books only arrive if the server nests them or
   * as targeted attach-request replies via `NotificationsService`).
   * When the server did not nest `book`, the gate returned false
   * forever and no HTTP request was ever fired for a 0→>0 quantity
   * bump — the cart line silently stayed at $0.00. See
   * 2026-07-09-cart-page-rates-never-fire.
   *
   * The rate endpoint's request body contract (see
   * `docs/contracts/inbox/_processed/2026-05-06-rateconfig-and-productionrates-endpoint.md`)
   * explicitly accepts `thickness: 0`, `weight: 0`, `caseQuantity: 0`
   * and empty laminate/paper — the server has the Product row indexed
   * by the `id` field and can join to Book itself. All the Item
   * accessors that back the payload already degrade to the
   * `product<Field>` snapshot columns on the Product row when `book`
   * is absent, so we're never sending garbage; unknown fields simply
   * fall through to the server-side defaults.
   *
   * Returning `true` unconditionally restores the pre-regression
   * behavior. Drafts still gate on `draftBody` / `draftCover` because
   * those services ARE loaded by the startup path
   * (`draftBodyService` / `draftCoverService`) and the accessors do
   * throw when their linked rows are missing.
   */
  hasCompleteRateInputs(): boolean {
    return true;
  }
}

